import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import { useChargePointGetRemoteStartTagsQuery } from '../../redux/api/web-api'
import useKeyboardHandle from '../../hooks/useKeyboardHandle'
import { Combobox, ComboboxOption } from '../combobox'
import Text from '../../../ui/Text'
import useText from '../../hooks/useText'

export type SelectRFIDTagModalProps = {
    chargePointId: string
    connectorId: number
    isOpen: boolean
    onStartCharging: (rfidTag: string) => void
    onClose: () => void
}

const ModalSubHeading = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: ${({ theme }) => theme.font.midDarkColor};
    margin: 55px 0px 15px 0px;
`

export const SelectRFIDTagModal: FC<SelectRFIDTagModalProps> = ({
    isOpen,
    chargePointId,
    connectorId,
    onClose,
    onStartCharging,
}) => {
    const text = useText()
    const [selectedRfid, setSelectedRfid] = useState('')
    const { data: rfidTags = [], isLoading } = useChargePointGetRemoteStartTagsQuery({ chargePointId, connectorId })

    const rfidTagsOptions = useMemo(
        () =>
            rfidTags.map<ComboboxOption<string>>((tag) => ({
                value: tag.id || '',
                label: `${tag.chargePointUserId ? tag.fullName : tag.companyName} - ${tag.id}`,
            })),
        [rfidTags]
    )

    useEffect(() => {
        if (rfidTags.length > 0) setSelectedRfid(rfidTags[0]?.id ?? '')
    }, [rfidTags])

    useKeyboardHandle({
        onEnterPress: () => {
            if (selectedRfid) onStartCharging(selectedRfid)
        },
        onEscapePress: onClose,
    })

    const renderContent = () => {
        if (isLoading) return <div data-testid="shimmer-loading" className="shimmer-container shimmer-small" />
        if (rfidTags.length > 0)
            return (
                <Combobox
                    id="input-rfid-tag"
                    enableDefaultOption={false}
                    value={selectedRfid}
                    options={rfidTagsOptions}
                    onChange={(option) => {
                        setSelectedRfid(option.value)
                    }}
                    data-testid="rfid-tag-select"
                />
            )

        return <Text code="msgNoRfidTagsAvailable" />
    }

    return isOpen ? (
        <BootstrapModalLayout
            size="md"
            title={text('lblRemoteStartCharging')}
            actions={[
                {
                    label: text('btnCancel'),
                    btn: 'btn-secondary',
                    minWidth: '180px',
                    callback: onClose,
                    dataTestId: 'btn-cancel',
                },
                {
                    label: text('btnStartCharging'),
                    minWidth: '180px',
                    disabled: isLoading || rfidTags.length === 0,
                    dataTestId: 'btn-on-starting-charging',
                    callback: () => onStartCharging(selectedRfid),
                },
            ]}
        >
            <ModalSubHeading data-testid="modal-sub-heading">{text('lblSelectRFIDToStartCharging')}</ModalSubHeading>
            <div className="margin-bottom-xlarge">{renderContent()}</div>
        </BootstrapModalLayout>
    ) : null
}
